import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import {
  AddFormComponent,
  FormField,
  ActionButton,
  TableSection,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  POR_ADD_FORM_FIELDS,
  POR_DETAIL_TABLE_SECTION,
  PurchaseReturnOrderDetailItem,
  PurchaseReturnOrderItem,
} from '../purchase-return-order.config';
import { PurchaseReturnOrderService } from '../purchase-return-order.service';
import {
  getPORStatusSeverity,
  getPORStatusLabel,
  getPORStatusStyle,
} from '../purchase-return-order.config';
import { parsePositiveIntRouteParam } from '@shared/utils/route-param.util';

@Component({
  selector: 'app-purchase-return-order-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    ButtonModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './purchase-return-order-view.component.html',
  styleUrl: './purchase-return-order-view.component.css',
})
export class PurchaseReturnOrderViewComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private porService = inject(PurchaseReturnOrderService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();

  // Form configuration
  formFields = signal<FormField[]>([]);
  headerActions = signal<ActionButton[]>([
    {
      label: 'common.actions.back',
      severity: 'secondary',
      action: 'back',
      outlined: true,
    },
  ]);

  // Data
  detailList = signal<PurchaseReturnOrderDetailItem[]>([]);
  loading = signal(false);
  currentFormData: any = null;
  porId: number | null = null;

  // Computed table sections - convert input fields to text for view mode
  tableSections = computed(() => {
    const section: TableSection = {
      ...POR_DETAIL_TABLE_SECTION,
      data: this.detailList(),
      show: true,
      hasCheckbox: false, // Remove checkbox in view mode
      actions: [], // Remove actions in view mode
      columns: POR_DETAIL_TABLE_SECTION.columns
        ?.map((col): TableColumn => {
          // Convert inputNumber to number, inputText to text
          if (col.type === 'inputNumber') {
            return {
              ...col,
              type: 'number' as const,
            };
          }
          if (col.type === 'inputText') {
            return {
              ...col,
              type: 'text' as const,
            };
          }
          return col;
        }) || [],
    };
    return [section];
  });

  ngOnInit(): void {
    this.initializeForm();
    this.loadPOR();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    // Make all fields readonly for view mode
    // Convert dropdown/searchableDropdown to text for view mode
    // Remove required indicator (asterisk) in view mode
    const viewFields = POR_ADD_FORM_FIELDS.map((field) => {
      const shouldConvertToText =
        (field.type === 'dropdown' || field.type === 'searchableDropdown');
      
      return {
        ...field,
        type: shouldConvertToText ? 'text' : field.type,
        readonly: true,
        disabled: true,
        required: false, // Remove required indicator in view mode
      };
    });
    this.formFields.set(viewFields);
  }

  private loadPOR(): void {
    const id = parsePositiveIntRouteParam(this.route.snapshot.paramMap.get('id'));
    if (id == null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_return_order.messages.invalid_id'
        ),
      });
      this.router.navigate(['/procurement/purchase-return-order']);
      return;
    }

    this.porId = id;
    this.loading.set(true);

    this.porService
      .getPORDetailWithItems(this.porId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ por, items }) => {
          this.populateForm(por);
          this.detailList.set(items);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading POR:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_return_order.messages.load_error'
            ),
          });
          this.loading.set(false);
          this.router.navigate(['/procurement/purchase-return-order']);
        },
      });
  }

  private populateForm(por: PurchaseReturnOrderItem): void {
    // For view mode, display text values instead of IDs
    // Since dropdowns are converted to text, we show the display names
    this.currentFormData = {
      porNo: por.porNo || por.rtvNo || '',
      poId: por.poCode || '', // Display PO code as text
      vendorId: por.vendorName || por.vendorCode || '', // Display vendor name as text
      warehouseId: por.warehouseName || por.warehouseCode || '', // Display warehouse name as text
      reason: por.reason || '',
      returnDate: por.returnDate || null,
    };
  }


  onFormAction(action: string): void {
    if (action === 'back') {
      this.router.navigate(['/procurement/purchase-return-order']);
    }
  }
}

